import { useMemo, useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconHistory,
  IconPlayerPlay,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useOrpc } from '@/hooks/useClient'
import { GuidelineBasis } from './GuidelineBasis'
import {
  CLUSTER_PROPOSALS_PAGE,
  CLUSTER_STATUS_COLORS,
  MEASURE_KIND_COLORS,
  actorLabel,
  useCpgClusterProposals,
  useCpgDocument,
  useCpgFamilies,
  useCpgProposalClusters,
  type CpgFamilyGroup,
  type CpgProposalCluster,
} from './hooks'

type ClusterState = 'open' | 'accepted' | 'dismissed'

const STATE_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'dismissed', label: 'Dismissed' },
]

/** Threshold a Run clustering button run uses — the task's default (cpg.proposals.cluster). */
const MIN_PATIENTS = 5

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—')

function ClusterList({
  title,
  clusters,
  selectedId,
  onSelect,
}: {
  /** Section heading (the open list splits recommended from below-threshold). */
  title?: string
  clusters: CpgProposalCluster[]
  selectedId: number | null
  onSelect: (id: number) => void
}) {
  // The server orders by family, then distinct patients — group consecutive rows.
  const families: { label: string; clusters: CpgProposalCluster[] }[] = []
  for (const cluster of clusters) {
    const last = families.at(-1)
    if (last?.label === cluster.familyLabel) last.clusters.push(cluster)
    else families.push({ label: cluster.familyLabel, clusters: [cluster] })
  }
  return (
    <nav className="p-2" aria-label={title}>
      {title && (
        <Text size="sm" fw={600} className="px-2 pb-1">
          {title} ({clusters.length})
        </Text>
      )}
      {title && clusters.length === 0 && (
        <Text size="xs" c="dimmed" className="px-2">
          None
        </Text>
      )}
      {families.map((family) => (
        <div key={family.label}>
          <div className="px-2 py-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            {family.label}
          </div>
          {family.clusters.map((cluster) => (
            <button
              key={cluster.id}
              type="button"
              onClick={() => onSelect(cluster.id)}
              className={`flex w-full flex-col gap-0.5 rounded px-2 py-1 pl-4 text-left text-sm hover:bg-gray-100 ${
                selectedId === cluster.id ? 'bg-gray-200 font-medium' : ''
              }`}
            >
              <span className="truncate">{cluster.label}</span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Badge
                  size="xs"
                  variant="light"
                  radius="sm"
                  color={CLUSTER_STATUS_COLORS[cluster.status]}
                >
                  {cluster.status}
                </Badge>
                {cluster.distinctPatients} patient{cluster.distinctPatients === 1 ? '' : 's'}
                {cluster.relatedMeasure && ' · review measure'}
                {cluster.suggestedCitations.length === 0 && ' · needs source'}
                {cluster.previousCluster && ' · recurring'}
              </span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  )
}

/** One page of member rows; the last loaded page offers the next one when there is more. */
function ClusterMemberPage({
  clusterId,
  offset,
  onMore,
}: {
  clusterId: number
  offset: number
  /** Set on the last loaded page only. */
  onMore?: () => void
}) {
  const query = useCpgClusterProposals(clusterId, offset)
  if (query.isError) {
    return (
      <Table.Tr>
        <Table.Td colSpan={4}>
          <Text size="xs" c="red">
            Couldn’t load the proposals.
          </Text>
        </Table.Td>
      </Table.Tr>
    )
  }
  return (
    <>
      {(query.data?.data ?? []).map((p) => (
        <Table.Tr key={p.id}>
          <Table.Td>{p.label}</Table.Td>
          <Table.Td>{p.familyLabel ?? 'unknown'}</Table.Td>
          <Table.Td className="whitespace-nowrap">{day(p.generatedAt)}</Table.Td>
          <Table.Td>
            <Text size="xs" lineClamp={3}>
              {p.definition}
            </Text>
          </Table.Td>
        </Table.Tr>
      ))}
      {onMore && query.data?.page.more && (
        <Table.Tr>
          <Table.Td colSpan={4}>
            <Button size="compact-xs" variant="light" onClick={onMore}>
              Load more
            </Button>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  )
}

/** Every member proposal, a page at a time — an admin decides the whole cluster from here. */
function ClusterMembers({ cluster }: { cluster: CpgProposalCluster }) {
  const [pages, setPages] = useState(1)
  return (
    <div className="mt-3">
      <Text size="sm" fw={600} tt="uppercase" c="dimmed" className="mb-1">
        Proposals ({cluster.proposalCount})
      </Text>
      <Table striped withTableBorder fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Proposed as</Table.Th>
            <Table.Th>Family</Table.Th>
            <Table.Th>Report</Table.Th>
            <Table.Th>Definition</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {Array.from({ length: pages }, (_, page) => (
            <ClusterMemberPage
              key={page}
              clusterId={cluster.id}
              offset={page * CLUSTER_PROPOSALS_PAGE}
              onMore={page === pages - 1 ? () => setPages(pages + 1) : undefined}
            />
          ))}
        </Table.Tbody>
      </Table>
    </div>
  )
}

/** A guideline passage chosen as a citation, with what the checklist shows for it. */
interface ChosenCitation {
  unitId: number
  label: string
}

/** Guideline matches per step of the citation picker, and the most it lists (the page cap). */
const PICKER_PAGE = 20
const PICKER_MAX = 100

/** Pick a passage from the active guideline corpus: a document, then one of its units. */
function CitationPicker({ onAdd }: { onAdd: (citation: ChosenCitation) => void }) {
  const orpc = useOrpc()
  const [search, setSearch] = useState('')
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [shown, setShown] = useState(PICKER_PAGE)
  const term = search.trim()
  const documents = useQuery(
    orpc.listCpgDocuments.queryOptions({
      input: {
        page: { offset: 0, count: shown },
        filters: [
          { field: 'status', operator: 'eq', operand: 'active' },
          ...(term.length >= 2
            ? [{ field: 'title' as const, operator: 'contains' as const, operand: term }]
            : []),
        ],
        order: { field: 'title', dir: 'asc' },
        scope: 'all',
      },
    }),
  )
  const doc = useCpgDocument(documentId === null ? null : Number(documentId)).data?.data

  return (
    <Stack gap={6}>
      <Select
        label="Cite another passage"
        placeholder="Search active guidelines by title"
        searchable
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setShown(PICKER_PAGE)
        }}
        data={(documents.data?.data ?? []).map((d) => ({
          value: String(d.id),
          label: `${d.source.toUpperCase()} · ${d.title} (${d.version})`,
        }))}
        value={documentId}
        onChange={setDocumentId}
        nothingFoundMessage="No active guideline matches"
      />
      {documents.data?.page.more &&
        (shown < PICKER_MAX ? (
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => setShown(shown + PICKER_PAGE)}
          >
            Showing the first {shown} matches — show more
          </Anchor>
        ) : (
          <Text size="xs" c="dimmed">
            Showing the first {shown} matches — type more of the title to narrow them.
          </Text>
        ))}
      {doc && (
        <div className="max-h-56 overflow-y-auto rounded border border-gray-200">
          {doc.units.map((unit) => (
            <div
              key={unit.id}
              className="flex items-start gap-2 border-b border-gray-100 px-2 py-1"
            >
              <Badge
                variant="outline"
                color="dark"
                radius="sm"
                size="xs"
                className="mt-0.5 shrink-0"
              >
                {unit.gradeOriginal ? `Grade ${unit.gradeOriginal}` : unit.kind}
              </Badge>
              <Text size="xs" className="min-w-0 flex-1" lineClamp={2}>
                {unit.body}
              </Text>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() =>
                  onAdd({
                    unitId: unit.id,
                    label: `${doc.source.toUpperCase()} · ${doc.title} — ${
                      unit.gradeOriginal ? `Grade ${unit.gradeOriginal}` : unit.kind
                    }: ${unit.body.slice(0, 120)}`,
                  })
                }
              >
                Cite
              </Button>
            </div>
          ))}
        </div>
      )}
    </Stack>
  )
}

function AcceptClusterModal({
  cluster,
  groups,
  onClose,
}: {
  cluster: CpgProposalCluster
  groups: CpgFamilyGroup[]
  onClose: () => void
}) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [familyId, setFamilyId] = useState<string | null>(String(cluster.familyId))
  const [slug, setSlug] = useState(cluster.slug)
  const [label, setLabel] = useState(cluster.label)
  const [definition, setDefinition] = useState(cluster.definition)
  const [gradingRule, setGradingRule] = useState(cluster.gradingRule)
  const [citations, setCitations] = useState<ChosenCitation[]>(() =>
    cluster.suggestedCitations.map((c) => ({
      unitId: c.unitId,
      label: `${c.source.toUpperCase()} · ${c.documentTitle} — ${
        c.gradeOriginal ? `Grade ${c.gradeOriginal}` : c.unitKind
      }: ${c.excerpt}`,
    })),
  )
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(cluster.suggestedCitations.map((c) => c.unitId)),
  )

  const accept = useMutation(
    orpc.acceptCpgProposalCluster.mutationOptions({
      onSuccess: async ({ measure }) => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.listCpgProposalClusters.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.listCpgMeasures.key() }),
        ])
        onClose()
        void navigate(`/super/cpg/measures?measure=${measure.id}`)
      },
    }),
  )

  const familyOptions = groups.map((g) => ({
    group: g.label,
    items: g.families.filter((f) => f.active).map((f) => ({ value: String(f.id), label: f.label })),
  }))
  const slugOk = /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)
  const unitIds = citations.filter((c) => checked.has(c.unitId)).map((c) => c.unitId)
  const ready =
    familyId !== null &&
    slugOk &&
    label.trim() !== '' &&
    definition.trim() !== '' &&
    gradingRule.trim() !== '' &&
    unitIds.length > 0

  const addCitation = (citation: ChosenCitation) => {
    setCitations((cur) =>
      cur.some((c) => c.unitId === citation.unitId) ? cur : [...cur, citation],
    )
    setChecked((cur) => new Set(cur).add(citation.unitId))
  }

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>Accept as a new measure</Text>} size="xl">
      <Stack gap="sm">
        <Select
          label="Family"
          data={familyOptions}
          value={familyId}
          onChange={setFamilyId}
          searchable
        />
        <TextInput
          label="Slug"
          description="kebab-case, the agent-facing identity — immutable once created"
          value={slug}
          onChange={(e) => setSlug(e.currentTarget.value)}
          error={slug !== '' && !slugOk ? 'kebab-case: a-z, 0-9, dashes' : undefined}
        />
        <TextInput label="Label" value={label} onChange={(e) => setLabel(e.currentTarget.value)} />
        <Textarea
          label="Definition"
          description="What qualifies a patient — read by the Risks & Gaps agent"
          autosize
          minRows={3}
          value={definition}
          onChange={(e) => setDefinition(e.currentTarget.value)}
        />
        <Textarea
          label="Grading rule"
          description="When a finding is high vs medium — read by the agent"
          autosize
          minRows={2}
          value={gradingRule}
          onChange={(e) => setGradingRule(e.currentTarget.value)}
        />
        <div>
          <Text size="sm" fw={500}>
            Citations
          </Text>
          <Text size="xs" c="dimmed" className="mb-1">
            At least one guideline passage — nothing enters the registry without a guideline behind
            it.
          </Text>
          {citations.length === 0 ? (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />}>
              No member proposal cites a passage. Pick one below.
            </Alert>
          ) : (
            <Stack gap={4}>
              {citations.map((c) => (
                <Checkbox
                  key={c.unitId}
                  size="xs"
                  label={c.label}
                  checked={checked.has(c.unitId)}
                  onChange={(e) => {
                    const on = e.currentTarget.checked
                    setChecked((cur) => {
                      const next = new Set(cur)
                      if (on) next.add(c.unitId)
                      else next.delete(c.unitId)
                      return next
                    })
                  }}
                />
              ))}
            </Stack>
          )}
        </div>
        <CitationPicker onAdd={addCitation} />
        {accept.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {accept.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            loading={accept.isPending}
            onClick={() =>
              accept.mutate({
                id: cluster.id,
                familyId: Number(familyId),
                slug,
                label: label.trim(),
                definition: definition.trim(),
                gradingRule: gradingRule.trim(),
                unitIds,
              })
            }
          >
            Accept
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function DismissClusterModal({
  cluster,
  onClose,
}: {
  cluster: CpgProposalCluster
  onClose: () => void
}) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const dismiss = useMutation(
    orpc.dismissCpgProposalCluster.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.listCpgProposalClusters.key() })
        onClose()
      },
    }),
  )
  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>Dismiss this cluster?</Text>}>
      <Stack gap="sm">
        <Text size="sm">
          Its {cluster.proposalCount} proposal{cluster.proposalCount === 1 ? '' : 's'} leave the
          pool. If matching proposals keep arriving, the next clustering run groups them into a new
          cluster linked back to this one, recommended again only once it reaches the patient
          threshold on its own.
        </Text>
        {dismiss.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {dismiss.error.message}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={dismiss.isPending}
            onClick={() => dismiss.mutate({ id: cluster.id })}
          >
            Dismiss
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/**
 * A below-threshold cluster is not a recommendation: accepting one is allowed, but on purpose.
 * The cluster doesn't record its run's minPatients, so the reason names the cause, not the
 * number.
 */
function ConfirmBelowThresholdModal({
  cluster,
  onCancel,
  onConfirm,
}: {
  cluster: CpgProposalCluster
  onCancel: () => void
  onConfirm: () => void
}) {
  const patients = `${cluster.distinctPatients} distinct patient${cluster.distinctPatients === 1 ? '' : 's'}`
  return (
    <Modal opened onClose={onCancel} title={<Text fw={600}>This cluster is not recommended</Text>}>
      <Stack gap="sm">
        <Text size="sm">
          {cluster.relatedMeasure
            ? `The clusterer thinks “${cluster.relatedMeasure.label}” may already cover it — review that measure’s wording first.`
            : `It is backed by only ${patients} — below the clustering run’s recommendation threshold.`}{' '}
          Accept it as a new measure anyway?
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Accept anyway</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function ClusterDetail({
  cluster,
  groups,
}: {
  cluster: CpgProposalCluster
  groups: CpgFamilyGroup[]
}) {
  const [deciding, setDeciding] = useState<'confirm' | 'accept' | 'dismiss' | null>(null)
  const open = cluster.status === 'recommended' || cluster.status === 'below-threshold'

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-4">
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs">
            <Text fw={600}>{cluster.label}</Text>
            <Badge variant="light" color="blue" radius="sm">
              {cluster.slug}
            </Badge>
            <Badge variant="light" color={MEASURE_KIND_COLORS[cluster.kind]} radius="sm">
              {cluster.kind}
            </Badge>
            <Badge variant="light" color={CLUSTER_STATUS_COLORS[cluster.status]} radius="sm">
              {cluster.status}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" className="mt-1">
            {cluster.familyLabel} · {cluster.distinctPatients} distinct patient
            {cluster.distinctPatients === 1 ? '' : 's'} · {cluster.proposalCount} proposal
            {cluster.proposalCount === 1 ? '' : 's'} · seen {day(cluster.firstSeenAt)} –{' '}
            {day(cluster.lastSeenAt)} · clustering run #{cluster.taskRunId}
          </Text>
        </div>
        {open && (
          <Group gap="xs">
            <Button
              size="compact-sm"
              onClick={() => setDeciding(cluster.status === 'recommended' ? 'accept' : 'confirm')}
            >
              Accept
            </Button>
            <Button
              size="compact-sm"
              variant="default"
              color="red"
              onClick={() => setDeciding('dismiss')}
            >
              Dismiss
            </Button>
          </Group>
        )}
      </Group>

      <Stack gap="xs" className="mt-3">
        {cluster.relatedMeasure && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              Review{' '}
              <Anchor
                component={Link}
                to={`/super/cpg/measures?measure=${cluster.relatedMeasure.id}`}
              >
                {cluster.relatedMeasure.label}
              </Anchor>{' '}
              first — it may already cover these proposals. {cluster.relatedReason}
            </Text>
          </Alert>
        )}
        {cluster.previousCluster && (
          <Alert color="gray" icon={<IconHistory size={16} />}>
            Came back after “{cluster.previousCluster.label}” was dismissed on{' '}
            {day(cluster.previousCluster.decidedAt)}.
          </Alert>
        )}
        {cluster.status === 'accepted' && cluster.measure && (
          <Text size="sm">
            Accepted by {actorLabel(cluster.decidedBy)} on {day(cluster.decidedAt)} as{' '}
            <Anchor component={Link} to={`/super/cpg/measures?measure=${cluster.measure.id}`}>
              {cluster.measure.label}
            </Anchor>
            .
          </Text>
        )}
        {cluster.status === 'dismissed' && (
          <Text size="sm">
            Dismissed by {actorLabel(cluster.decidedBy)} on {day(cluster.decidedAt)}.
          </Text>
        )}
      </Stack>

      {[
        ['Definition', cluster.definition],
        ['Grading rule', cluster.gradingRule],
        ['Why these belong together', cluster.rationale],
      ].map(([title, body]) => (
        <div key={title} className="mt-3">
          <Text size="sm" fw={600} tt="uppercase" c="dimmed">
            {title}
          </Text>
          <Text size="sm" className="whitespace-pre-wrap">
            {body}
          </Text>
        </div>
      ))}

      <GuidelineBasis
        citations={cluster.suggestedCitations}
        title="Suggested citations"
        empty="Needs a guideline source — no member proposal cites a guideline passage. Pick one when accepting, or leave the cluster open until a source can back it."
      />
      <ClusterMembers cluster={cluster} />

      {deciding === 'confirm' && (
        <ConfirmBelowThresholdModal
          cluster={cluster}
          onCancel={() => setDeciding(null)}
          onConfirm={() => setDeciding('accept')}
        />
      )}
      {deciding === 'accept' && (
        <AcceptClusterModal cluster={cluster} groups={groups} onClose={() => setDeciding(null)} />
      )}
      {deciding === 'dismiss' && (
        <DismissClusterModal cluster={cluster} onClose={() => setDeciding(null)} />
      )}
    </div>
  )
}

function RunClustering() {
  const orpc = useOrpc()
  const run = useMutation(orpc.runTask.mutationOptions())
  return (
    <Stack gap={4}>
      <Button
        fullWidth
        size="compact-sm"
        variant="light"
        leftSection={<IconPlayerPlay size={14} />}
        loading={run.isPending}
        onClick={() =>
          run.mutate({ slug: 'cpg.proposals.cluster', args: { minPatients: MIN_PATIENTS } })
        }
      >
        Run clustering
      </Button>
      {run.data && (
        <Text size="xs" c="dimmed">
          Run #{run.data.id} queued — follow it in{' '}
          <Anchor component={Link} to="/super/tasks" size="xs">
            Tasks
          </Anchor>
          , then reload this list.
        </Text>
      )}
      {run.error && (
        <Text size="xs" c="red">
          {run.error.message}
        </Text>
      )}
    </Stack>
  )
}

/**
 * Super Admin → CPG → Measures → Proposals (PD-1716, docs/cpg/CPG_ADMIN.md): the clusters the
 * cpg.proposals.cluster run made of the agent's accumulated measure proposals. Open clusters
 * are decided here — Accept (a new measure, at least one citation) or Dismiss; there is no
 * per-proposal decision. A cluster related to an active measure is a hint to sharpen that
 * measure first (then dismiss, and see whether proposals keep coming).
 */
export function ProposalsView() {
  const [state, setState] = useState<ClusterState>('open')
  const clustersQuery = useCpgProposalClusters(state)
  const familiesQuery = useCpgFamilies()
  const clusters = useMemo(() => clustersQuery.data?.data ?? [], [clustersQuery.data])
  const groups = useMemo(() => familiesQuery.data?.data ?? [], [familiesQuery.data])
  // Open clusters split into what the run recommends and the rest; decided ones are one list.
  const sections = useMemo(
    () =>
      state === 'open'
        ? [
            { title: 'Recommended', clusters: clusters.filter((c) => c.status === 'recommended') },
            {
              title: 'Below threshold',
              clusters: clusters.filter((c) => c.status === 'below-threshold'),
            },
          ]
        : [{ title: undefined, clusters }],
    [state, clusters],
  )
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const selected =
    clusters.find((c) => c.id === selectedId) ?? sections.flatMap((s) => s.clusters)[0] ?? null

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-80 shrink-0 flex-col border-r border-gray-200">
        <div className="shrink-0 border-b border-gray-200 p-2">
          <SegmentedControl
            fullWidth
            size="xs"
            value={state}
            onChange={(value) => {
              setState(value as ClusterState)
              setSelectedId(null)
            }}
            data={STATE_OPTIONS}
          />
        </div>
        {clustersQuery.isError && (
          <Alert
            m="xs"
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            title="Couldn’t load the proposal clusters"
          >
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={() => void clustersQuery.refetch()}
            >
              Try again
            </Button>
          </Alert>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {clusters.length > 0 &&
            sections.map((section) => (
              <ClusterList
                key={section.title ?? state}
                title={section.title}
                clusters={section.clusters}
                selectedId={selected?.id ?? null}
                onSelect={setSelectedId}
              />
            ))}
        </div>
        <div className="shrink-0 border-t border-gray-200 p-2">
          <RunClustering />
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        {selected ? (
          <ClusterDetail key={selected.id} cluster={selected} groups={groups} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            {clustersQuery.isLoading
              ? 'Loading…'
              : clustersQuery.isError
                ? 'Couldn’t load clusters.'
                : state === 'open'
                  ? 'No open clusters — run clustering to group the pending proposals.'
                  : `No ${state} clusters yet.`}
          </div>
        )}
      </main>
    </div>
  )
}
